/**
 * RCT (Recovery / Claim Tracking) Store — replaces RCT-related `$rootScope` flags.
 *
 * Keys:
 *   RCTCalloutCalled       — has the RCT callout been triggered for the current order
 *   RCTStateClean          — whether the RCT state is "clean" (no pending claim edits)
 *   doNotShowRCT           — feature gate: hide RCT panels for the current user/work-order
 *   allowedClaimStatuses   — list of claim statuses available in the current workflow
 */

import { create } from 'zustand';
import { setRootScopeProperty, getRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

interface RctState {
  rctCalloutCalled: boolean;
  rctStateClean: boolean;
  doNotShowRct: boolean;
  // Mirrors AngularJS SidebarController `$scope.rctAlwaysEnable` — web.config
  // flag that force-enables (green + never-disabled) the RCT Call Out button
  // regardless of audit counts. Set from getRCTAlwaysEnableKey() during the
  // RCT determination (sidebar.js:607-609).
  rctAlwaysEnable: boolean;
  allowedClaimStatuses: unknown[];
}

interface RctActions {
  setRctCalloutCalled: (v: boolean) => void;
  setRctStateClean: (v: boolean) => void;
  setDoNotShowRct: (v: boolean) => void;
  setRctAlwaysEnable: (v: boolean) => void;
  setAllowedClaimStatuses: (v: unknown[]) => void;
  hydrateFromLegacy: () => void;
  reset: () => void;
}

type RctStore = RctState & RctActions;

const INITIAL_STATE: RctState = {
  rctCalloutCalled: false,
  rctStateClean: false,
  // Matches AngularJS SidebarController default — `$scope.doNotShowRCT = true`
  // (public/layout/sidebar.js:19). Flipped to `false` only when the
  // CoreLogicRCTIntegration luggage check + survey answers pass. That
  // determination is ported in AppSidebar `runRctDetermination` (sidebar.js
  // getClientAttributeValues, lines 565-611), invoked from the main context
  // effect. Default stays true so RCT enforcement is dormant until the check runs.
  doNotShowRct: true,
  rctAlwaysEnable: false,
  allowedClaimStatuses: [],
};

function syncToLegacy(key: string, value: unknown): void {
  try {
    if (getMountConfig().mode === 'embedded') {
      setRootScopeProperty(key, value);
    }
  } catch {
    // graceful
  }
}

export const useRctStore = create<RctStore>()((set) => ({
  ...INITIAL_STATE,

  setRctCalloutCalled: (v) => {
    set({ rctCalloutCalled: v });
    syncToLegacy('RCTCalloutCalled', v);
  },
  setRctStateClean: (v) => {
    set({ rctStateClean: v });
    syncToLegacy('RCTStateClean', v);
  },
  setDoNotShowRct: (v) => {
    set({ doNotShowRct: v });
    syncToLegacy('doNotShowRCT', v);
  },
  setRctAlwaysEnable: (v) => {
    set({ rctAlwaysEnable: v });
    syncToLegacy('rctAlwaysEnable', v);
  },
  setAllowedClaimStatuses: (v) => {
    set({ allowedClaimStatuses: v });
    syncToLegacy('allowedClaimStatuses', v);
  },

  hydrateFromLegacy: () => {
    // Embedded-only — see propertyStore.hydrateFromLegacy for full reasoning.
    if (getMountConfig().mode !== 'embedded') return;
    // doNotShowRCT lives on SidebarController `$scope`, not `$rootScope`
    // (sidebar.js:19) — so `$rootScope.doNotShowRCT` is almost always
    // undefined. Only override the default-true when the legacy value is
    // explicitly defined; otherwise preserve the safe default.
    const legacyDoNotShowRct = getRootScopeProperty<boolean>('doNotShowRCT');
    set({
      rctCalloutCalled: Boolean(getRootScopeProperty<boolean>('RCTCalloutCalled')),
      rctStateClean: Boolean(getRootScopeProperty<boolean>('RCTStateClean')),
      ...(legacyDoNotShowRct !== undefined && legacyDoNotShowRct !== null
        ? { doNotShowRct: Boolean(legacyDoNotShowRct) }
        : {}),
      rctAlwaysEnable: Boolean(getRootScopeProperty<boolean>('rctAlwaysEnable')),
      allowedClaimStatuses: getRootScopeProperty<unknown[]>('allowedClaimStatuses') ?? [],
    });
  },

  reset: () => set({ ...INITIAL_STATE }),
}));

export const useRctCalloutCalled = (): boolean => useRctStore((s) => s.rctCalloutCalled);
export const useRctStateClean = (): boolean => useRctStore((s) => s.rctStateClean);
export const useDoNotShowRct = (): boolean => useRctStore((s) => s.doNotShowRct);
export const useRctAlwaysEnable = (): boolean => useRctStore((s) => s.rctAlwaysEnable);
export const useAllowedClaimStatuses = (): unknown[] => useRctStore((s) => s.allowedClaimStatuses);

/** Test-only. */
export function __resetRctStoreForTests(): void {
  useRctStore.setState({ ...INITIAL_STATE });
}
